import React, { useState } from 'react';
import './OrdersPage.scss';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import usePermission from '../../../shared/hooks/global/UsePermission';
import { useNavigate } from 'react-router-dom';
import usePaddleTransactions from '../../../shared/hooks/pricing/UsePaddleTransactions';
import Loader from '../../../shared/components/common/loader/Loader';
import { t } from 'i18next';
import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { getInvoice } from '../../../shared/services/pricing/PricingService';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import EmptyState from '../../../shared/components/common/empty-state/EmptyState';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../../shared/utils/mobile';

const OrdersPage: React.FC = () => {
  const { accountResponse } = useAccount();
  const billingAccess = usePermission('BILLING');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [transaction, setTransaction] = useState<any>(null);

  usePaddleTransactions(setTransaction);

  if (!accountResponse || !billingAccess.read) {
    navigate('/settings/my-account');
    return <></>;
  }

  if (transaction === null) {
    return <Loader />;
  }

  const generateInvoice = (transactionId: string) => {
    getInvoice(transactionId).then((response) => window.open(response.data.url, '_blank'));
  };

  const invoiceLinkTemplate = (data: any) => {
    const transactionId = data.transactionId;
    return (
      <>
        <Button
          id={`invoice-btn-${transactionId}`}
          className="action-btn"
          onClick={() => generateInvoice(transactionId)}
        >
          <span className="ico--download ico--xs" />
        </Button>
        <CustomTooltip tooltipText="download" targetRef={`invoice-btn-${transactionId}`} />
      </>
    );
  };

  const totalTemplate = (total: number) => {
    if (total === 0) {
      return '$0';
    }

    const value = total / 100;
    if (value < 0) {
      return `- $${Math.abs(value)}`;
    }

    return `$${value}`;
  };

  if (!transaction.data || transaction.data.length === 0) {
    return (
      <div className="orders-page">
        <EmptyState emptyStateData={{ title: t('emptyStateOrders'), imageClassName: 'img--project' }} />
      </div>
    );
  }

  return (
    <div className="orders-page">
      <DataTable
        stripedRows
        paginator={true}
        responsiveLayout="stack"
        paginatorClassName="pagination"
        removableSort
        sortField="invoice"
        sortOrder={-1}
        rows={10}
        value={transaction.data}
        rowsPerPageOptions={[10, 25, 50]}
        tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
        tableClassName="table"
        className="table-wrapper table-wrapper--stack orders-table"
        currentPageReportTemplate={t('rowsNumber') + ':'}
        paginatorTemplate={
          isMobile
            ? MOBILE_PAGINATOR_TEMPLATE
            : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
        }
        pageLinkSize={isMobile ? 3 : 5}
      >
        <Column
          sortable
          field="invoiceNumber"
          header={t('invoiceNumber')}
          headerClassName="table__header"
          style={{ width: '90px' }}
        />
        <Column
          field="plan"
          header={t('plan')}
          headerClassName="table__header"
          bodyClassName="table__cell table__cell--stack-title"
          style={{ minWidth: '250px' }}
        />
        <Column
          sortable
          field="date"
          header={t('date')}
          body={(data) => format(data.date, 'yyyy-MM-dd / HH:mm')}
          headerClassName="table__header"
          style={{ width: '160px' }}
        />
        <Column
          field="total"
          header={t('total')}
          body={(data) => totalTemplate(data.total)}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell table__cell--center"
          style={{ width: '120px' }}
        />
        <Column
          field="invoice"
          header={t('invoice')}
          body={invoiceLinkTemplate}
          headerClassName="table__header table__header--center"
          bodyClassName="table__cell--center table__cell--stack-actions"
          style={{ width: '80px' }}
        />
      </DataTable>
    </div>
  );
};

export default OrdersPage;
